import React, { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from './Header';
import SylloArchiveStrip from './SylloArchiveStrip';
import ClueCardSimple from './ClueCardSimple';
import SEO from './SEO';
import { useSyllo } from '../context/SylloContext';
import { Sparkles, ChevronLeft, ChevronRight } from 'lucide-react';
import NotFoundPage from './NotFoundPage';

import Sidebar from './Sidebar';

const SylloPage = ({ isToday, date: propDate }) => {
  const params = useParams();
  const { puzzles, loading } = useSyllo();
  
  const groupedPuzzles = useMemo(() => {
    const groups = {};
    puzzles.forEach(puzzle => {
      const date = new Date(puzzle.date);
      const key = date.toLocaleString('default', { month: 'long', year: 'numeric' });
      if (!groups[key]) {
        groups[key] = [];
      }
      if (groups[key].length < 4) {
        groups[key].push(puzzle);
      }
    });
    return groups;
  }, [puzzles]);
  
  if (loading) return null;

  let selectedPuzzle = null;

  if (isToday) {
    selectedPuzzle = puzzles.find(p => p.date === propDate);
  } else {
    // Try to find by number if URL matches syllo-X
    const numberMatch = params.puzzleId ? params.puzzleId.match(/syllo-(-?\d+)/) : null;
    if (numberMatch) {
      const number = numberMatch[1];
      selectedPuzzle = puzzles.find(p => p.puzzleNumber === number);
    } else {
         selectedPuzzle = puzzles.find(p => p.date === params.date);
    }
  }
  
  if (!selectedPuzzle && !isToday) return <NotFoundPage />;
  if (!selectedPuzzle) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#f8f9fa] p-4 text-center">
        <Header date={new Date().toISOString()} title="Syllo" />
        <div className="flex-grow flex items-center justify-center">
            <p className="text-gray-500 text-lg">No Syllo puzzle found for today. Please check back later.</p>
        </div>
      </div>
    );
  }

  const { date, title, clues } = selectedPuzzle;

  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": isToday ? `Today's Syllo Hints & Answers: ${title}` : `${title} Hints and Answers`,
    "description": `Get hints and answers for ${title} from ${new Date(date).toLocaleDateString()}. Solve the 5 clues to piece together the words.`,
    "image": `${window.location.origin}/pocket-grids-logo.png`, // Placeholder
    "datePublished": new Date(date).toISOString(),
    "dateModified": new Date(date).toISOString(),
    "author": {
      "@type": "Organization",
      "name": "PocketHints"
    },
    "publisher": {
      "@type": "Organization",
      "name": "PocketHints",
      "logo": {
        "@type": "ImageObject",
        "url": `${window.location.origin}/site-logo.png`
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": window.location.href
    }
  };

  const breadcrumbs = [
    { name: "Home", item: window.location.origin },
    { name: "Syllo Archive", item: `${window.location.origin}/syllo-archive` },
    { name: isToday ? "Today's Puzzle" : `${title}`, item: window.location.href }
  ];

  return (
    <div className="flex flex-col w-full pb-12">
      <SEO 
        title={isToday ? "Today's Syllo Hints & Answers" : `${title} - ${new Date(date).toLocaleDateString()}`}
        description={`Get hints and answers for ${title} from ${new Date(date).toLocaleDateString()}. Solve the 5 clues to piece together the words.`}
        url={window.location.href}
        schema={schema}
        breadcrumbs={breadcrumbs}
      />
      <Header date={date} title={title} gameName="Daily Syllo" />
      
      <SylloArchiveStrip 
        puzzles={puzzles} 
        selectedDate={date} 
      />


      {isToday && (
        <div className="max-w-4xl mx-auto px-4 mb-12 text-center">
          <div className="bg-white border border-gray-200 rounded-2xl p-6 md:p-8 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-4">
              Daily Syllo Hints & Answers
            </h2>
            <div className="prose prose-blue mx-auto text-gray-600 leading-relaxed mb-8 entry-content">
              <p className="mb-4">
                <strong>Today's Syllo Answers</strong> are ready for you! Stuck on today's Syllo? We've got the hints you need to merge those syllables!
              </p>
              <p className="mb-4">
                <strong>Syllo</strong> challenges you to merge syllables to solve clues. You get 5 clues to piece together 5 words from a jumble of discombobulated syllables.
              </p>
              <p className="mb-4">
                Puzzles start easy on Monday and get tougher each day, building to the trickiest challenge on Sunday.
                Race against the clock to climb the leaderboard!
              </p>
              <p className="text-xs text-gray-400 italic mt-4">
                Disclaimer: We are not affiliated with the creators of Syllo. We just love the game!
              </p>
            </div>

            <div className="text-center">
              <a 
                href="https://www.reddit.com/r/syllo/"
                target="_blank"
                rel="noopener noreferrer" 
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-brand-blue text-white text-lg font-bold rounded-xl shadow-lg hover:bg-blue-500 hover:scale-105 transition-all duration-200"
              >
                Play Syllo
              </a>
              <p className="mt-3 text-xs font-medium text-gray-400 uppercase tracking-wide">
                JOIN THE COMMUNITY
              </p>
            </div>
          </div>
        </div>
      )}
      
      <main className="flex-grow max-w-7xl mx-auto px-4 sm:px-6 w-full flex flex-col lg:flex-row gap-8">
        <div className="flex-1 min-w-0">
          <section className="w-full relative">
            <div className="sticky top-24 z-30 bg-white border-b border-gray-200 py-3 mb-6 lg:static lg:bg-transparent lg:border-none rounded-xl lg:rounded-none px-4 lg:px-0 shadow-sm lg:shadow-none">
              <h2 className="text-2xl font-black text-gray-900 flex items-center gap-3">
                Clues
              </h2>
            </div>
            
            <p className="text-gray-600 mb-6 leading-relaxed">
              Here are the clues for today's puzzle. Click "Hints" for a nudge or "Reveal Answer" if you're stumped.
            </p>


            <div className="space-y-4">
            {clues.map((clue) => (
              <ClueCardSimple 
                key={`${date}-${clue.id}`} 
                clue={clue} 
                date={isToday ? null : date} 
                isToday={isToday}
                puzzleNumber={selectedPuzzle.puzzleNumber}
                linkPrefix={isToday ? "/todays-syllo-clues-answers" : `/syllo/syllo-${selectedPuzzle.puzzleNumber}`}
              />
            ))}
            </div>
          </section>


          {/* Play More Section */}
          <section className="mt-8 mb-8 text-center bg-white rounded-3xl p-8 border border-gray-200 shadow-sm">
            <div className="flex flex-col items-center gap-6">
              <img 
                src={process.env.PUBLIC_URL + '/Syllo-logo.png'}
                alt="Syllo Logo" 
                className="w-24 h-24 object-contain drop-shadow-sm"
              />
              <h2 className="text-3xl font-black text-gray-900">
                Want to Solve More?
              </h2>
              <div className="flex flex-col items-center justify-center gap-4 w-full max-w-md">
                <Link 
                  to="/todays-pocket-grids-clues-answers"
                  className="flex items-center justify-between w-full px-6 py-4 bg-white text-brand-blue border-2 border-brand-blue text-lg font-bold rounded-xl shadow-sm hover:bg-brand-blue/5 hover:scale-105 transition-all duration-200"
                >
                  <div className="flex items-center gap-3">
                    <img src={process.env.PUBLIC_URL + '/pocket-grids-logo.png'} alt="Pocket Grids" className="w-8 h-8 object-contain" />
                    <span>Pocket Grids Hints</span>
                  </div>
                  <ChevronLeft className="w-5 h-5 rotate-180" />
                </Link>
                <Link 
                  to="/todays-hot-and-cold-clues-answers"
                  className="flex items-center justify-between w-full px-6 py-4 bg-white text-brand-blue border-2 border-brand-blue text-lg font-bold rounded-xl shadow-sm hover:bg-brand-blue/5 hover:scale-105 transition-all duration-200"
                >
                  <div className="flex items-center gap-3">
                    <img src={process.env.PUBLIC_URL + '/hotandcold-logo.png'} alt="Hot and Cold" className="w-8 h-8 object-contain" />
                    <span>Hot & Cold Hints</span>
                  </div>
                  <ChevronLeft className="w-5 h-5 rotate-180" />
                </Link>
                <Link 
                  to="/todays-byrdle-clues-answers"
                  className="flex items-center justify-between w-full px-6 py-4 bg-white text-brand-blue border-2 border-brand-blue text-lg font-bold rounded-xl shadow-sm hover:bg-brand-blue/5 hover:scale-105 transition-all duration-200"
                >
                  <div className="flex items-center gap-3">
                    <img src={process.env.PUBLIC_URL + '/byrdle-logo.png'} alt="Byrdle" className="w-8 h-8 object-contain" />
                    <span>Byrdle Hints</span>
                  </div>
                  <ChevronLeft className="w-5 h-5 rotate-180" />
                </Link>
                <Link 
                  to="/daily-guess-solver"
                  className="flex items-center justify-between w-full px-6 py-4 bg-white text-brand-blue border-2 border-brand-blue text-lg font-bold rounded-xl shadow-sm hover:bg-brand-blue/5 hover:scale-105 transition-all duration-200"
                >
                  <div className="flex items-center gap-3">
                    <img src={process.env.PUBLIC_URL + '/dailyguess-logo.png'} alt="DailyGuess" className="w-8 h-8 object-contain" />
                    <span>DailyGuess Solver</span>
                  </div>
                  <ChevronLeft className="w-5 h-5 rotate-180" />
                </Link>
              </div>
            </div>
          </section>

          {/* SEO Content Section */}
          <section className="mt-12 bg-white rounded-3xl p-8 border border-gray-200 shadow-sm">
            <h2 className="text-2xl font-black text-gray-900 mb-6">About Syllo</h2>
            
            <div className="space-y-8 entry-content">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">How does Syllo work?</h3>
                <p className="text-gray-600 leading-relaxed">
                  Syllo is a daily puzzle where you must merge syllables to solve 5 clues. You are given a jumble of syllables and you need to piece them together to form the correct words that answer the clues. The puzzles start easy on Monday and get progressively harder throughout the week.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Why we do this</h3>
                <p className="text-gray-600 leading-relaxed">
                  Sometimes those syllables just don't seem to fit together! We built this helper site to give you progressive hints for each clue. This way, you can get a little push in the right direction without having the entire puzzle spoiled for you.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Frequently Asked Questions</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-bold text-gray-800">When does the new Syllo puzzle come out?</h4>
                    <p className="text-gray-600">A new puzzle is released every day at 12 PM UTC.</p>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-800">Are the syllables always split the same way?</h4>
                    <p className="text-gray-600">The syllables are split in a way that makes the puzzle challenging but solvable. They don't always follow strict phonetic syllable rules, which is part of the fun!</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Archive Section */}
          <section className="mt-12 bg-white rounded-3xl p-8 border border-gray-200 shadow-sm">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-black text-gray-900 mb-4">Syllo Archive</h2>
              <p className="text-gray-600 text-lg">Browse our collection of past daily crossword guides.</p>
            </div>
            <div className="space-y-12">
              {Object.entries(groupedPuzzles).slice(0, 3).map(([monthYear, monthPuzzles]) => (
                <div key={monthYear} className="animate-fade-in-up">
                  <h3 className="text-xl font-bold text-gray-400 uppercase tracking-wider mb-6 border-b border-gray-200 pb-2">
                    {monthYear}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {monthPuzzles.map((puzzle) => {
                       const pDate = new Date(puzzle.date);
                       const dayName = pDate.toLocaleDateString('en-US', { weekday: 'long' });
                       const dayNum = pDate.getDate();
                       
                       return (
                        <Link 
                          key={puzzle.date} 
                          to={`/syllo/syllo-${puzzle.puzzleNumber || puzzle.date}`}
                          className="group bg-white border border-gray-200 rounded-xl p-5 hover:border-brand-blue hover:shadow-md transition-all flex items-center justify-between"
                        >
                          <div className="flex items-center gap-4">
                            <div className="bg-brand-blue/10 text-brand-blue font-bold text-xl w-12 h-12 rounded-lg flex items-center justify-center group-hover:bg-brand-blue group-hover:text-white transition-colors">
                              {dayNum}
                            </div>
                            <div>
                              <div className="font-bold text-gray-900">{puzzle.title}</div>
                              <div className="text-sm text-gray-500 font-medium">{dayName}</div>
                            </div>
                          </div>
                          <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-brand-blue transition-colors" />
                        </Link>
                       );
                    })}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-10 text-center">
              <Link 
                to="/syllo-archive"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gray-100 text-gray-900 text-lg font-bold rounded-xl shadow-sm hover:bg-gray-200 hover:scale-105 transition-all duration-200"
              >
                View Full Archive
              </Link>
            </div>
          </section>
        </div>

        {/* Sidebar (Desktop Only) */}
        <div className="hidden lg:block w-80 flex-shrink-0">
          <div>
            <Sidebar />
          </div>
        </div>
      </main>
    </div>
  );
};

export default SylloPage;

